// @ts-strict-ignore
import type {CurrentProject} from 'common/report/model-resources/projects';
import {ProjectDetails} from 'common/report/stores/project-details';
import {getProjectId, isCurrentProjectInternal, isUrlDefinedExperimentalFeatureEnabled} from 'common/report/util';
import {isDevEnv, isTestEnv} from 'common/report/util/env';
import {getLocation} from 'common/util/url';
import {FEATURE_FLAG_VARIANTS} from 'apps/feature-flags/src/constants';

import {FeatureFlag, FeatureGate, ProjectFeatureFlag} from '../constants';

export const featureSamplingUtils = {
  determineFeatureEligibility,
};

/**
 * Check deterministically whether a user or group is included in a feature rollout/test based on a
 * unique user/group string identifier and the desired percentage to include.
 */
function determineFeatureEligibility(
  userOrGroupId: string,
  feature: string,
  percentAllowed: number /* must be in range [0, 100] */,
): boolean {
  if (!(percentAllowed >= 0 && percentAllowed <= 100)) {
    throw new Error(`@percentAllowed argument must be in range [0, 100] inclusive`);
  }

  if (typeof userOrGroupId === `undefined` || userOrGroupId === null || userOrGroupId === ``) {
    throw new Error(`@userOrGroupId argument cannot be null or undefined or empty`);
  }
  if (typeof feature === `undefined` || feature === null || feature === ``) {
    throw new Error(`@feature argument cannot be null or undefined or empty`);
  }

  switch (percentAllowed) {
    case 0:
      return false;
    case 100:
      return true;
    default:
      break;
  }

  const key = userOrGroupId + feature;

  // Bernstein's hash: http://www.cse.yorku.ca/~oz/hash.html#djb2
  // The djb2 hash was chosen because of the claim that "it has excellent distribution and speed on many different sets of keys and table sizes",
  // but we have not validated whether it is statistically reliable and accurate to use in sampling users/groups for rollouts and analysis.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) + hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  const dart = (hash >>> 0) % 100;
  return dart < percentAllowed;
}

enum FeatureRolloutType {
  Project,
  Org,
}
export class FeatureRollout {
  private declare type: FeatureRolloutType;
  private declare percentage: number;
  public declare readonly featureflag: ProjectFeatureFlag;
  private explicitlyDisallowIds: string[] = [];

  public declare readonly name: FeatureRolloutName;

  constructor(params: {
    type: FeatureRolloutType;
    name: FeatureRolloutName;
    percentage: number;
    featureflag?: ProjectFeatureFlag;
    explicitlyDisallowIds?: string[];
  }) {
    this.type = params.type;
    this.name = params.name;
    this.percentage = params.percentage;
    this.featureflag = params.featureflag;
    this.explicitlyDisallowIds = params.explicitlyDisallowIds ?? [];
  }

  isEnabled(project: CurrentProject | ProjectDetails | null): boolean {
    if (this.type === FeatureRolloutType.Project) {
      const projectId = project?.id.toString() ?? getProjectId();
      if (this.explicitlyDisallowIds.includes(projectId)) {
        return false;
      }

      return projectId
        ? featureSamplingUtils.determineFeatureEligibility(projectId, this.name.toString(), this.percentage) ||
            project?.hasFeatureFlag(this.featureflag)
        : false;
    }
    const orgId = project?.organizationId.toString();
    if (this.explicitlyDisallowIds.includes(orgId)) {
      return false;
    }

    return orgId
      ? featureSamplingUtils.determineFeatureEligibility(orgId, this.name.toString(), this.percentage) ||
          project?.hasFeatureFlag(this.featureflag)
      : false;
  }
}

// Add any gradual feature rollouts here
export enum FeatureRolloutName {
  OnboardingChecklist = `onboarding-checklist`,
  Revenue = `revenue`,
  Home = `home`,
  WarehouseLaunchTemplates = `wh_launch_templates`,
  DisableVizOnNonCompliant = `disable_viz_non_comp`,
  CompanyProfile = `company_profile`,
  BoardBreakdowns = `board_breakdowns`,
  NullPropertyMonitoring = `null_property_monitoring`,
  OptimizedFunnelSelection = `optimized_funnels`,
  PropertyTypeMonitoring = `property_type_monitoring`,
  ConcurrentReplays = `sr_concurrent_replays`,
  TextAnalysis = `text_analysis`,
  IssuesHub = `issues_hub`,
  IssuesHubSidePanel = `issues_hub_sidepanel`,
  EnableCustomRoles = `enable_custom_roles`,
  EnableGovernedAIMode = `enable_governed_ai_mode`,
  EnableVerifiedCohorts = `enable_verified_cohorts`,
  WebhookAlerts = `webhook_alerts`,
  McpGaTour = `mcp_ga_tour`,
  CustomAlertsCharts = `custom_alerts_charts`,
  IntegrationsV2UI = `integrations_v2_ui`,
  AdvancedDropFilters = `advanced_drop_filters`,
}

export const FeatureRollouts: {[key in FeatureRolloutName]: FeatureRollout} = {
  [FeatureRolloutName.OnboardingChecklist]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.OnboardingChecklist,
    percentage: 0,
  }),
  [FeatureRolloutName.Revenue]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Revenue,
    featureflag: ProjectFeatureFlag.Revenue,
    percentage: 0,
  }),
  [FeatureRolloutName.Home]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Home,
    percentage: 100,
    explicitlyDisallowIds: [`1866253`],
  }),
  [FeatureRolloutName.WarehouseLaunchTemplates]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.WarehouseLaunchTemplates,
    percentage: 0,
    featureflag: ProjectFeatureFlag.WarehouseLaunchTemplates,
  }),
  [FeatureRolloutName.DisableVizOnNonCompliant]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.DisableVizOnNonCompliant,
    percentage: 0,
    featureflag: ProjectFeatureFlag.DisableVizOnNonCompliant,
  }),
  [FeatureRolloutName.CompanyProfile]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CompanyProfile,
    percentage: 100,
    featureflag: ProjectFeatureFlag.CompanyProfile,
  }),
  [FeatureRolloutName.ConcurrentReplays]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.ConcurrentReplays,
    percentage: 0,
    featureflag: ProjectFeatureFlag.ConcurrentReplays,
  }),
  [FeatureRolloutName.BoardBreakdowns]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.BoardBreakdowns,
    percentage: 100,
    featureflag: ProjectFeatureFlag.BoardBreakdowns,
  }),
  [FeatureRolloutName.NullPropertyMonitoring]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.NullPropertyMonitoring,
    percentage: 50,
    featureflag: ProjectFeatureFlag.NullPropertyMonitoring,
  }),
  [FeatureRolloutName.PropertyTypeMonitoring]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.PropertyTypeMonitoring,
    percentage: 100,
    featureflag: ProjectFeatureFlag.PropertyTypeMonitoring,
  }),
  [FeatureRolloutName.OptimizedFunnelSelection]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.OptimizedFunnelSelection,
    percentage: 0,
    featureflag: ProjectFeatureFlag.OptimizedFunnelSelection,
  }),
  [FeatureRolloutName.TextAnalysis]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.TextAnalysis,
    percentage: 0,
    featureflag: ProjectFeatureFlag.TextAnalysis,
  }),
  [FeatureRolloutName.IssuesHub]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.IssuesHub,
    percentage: 0,
    featureflag: ProjectFeatureFlag.LexiconIssuesHub,
  }),
  [FeatureRolloutName.IssuesHubSidePanel]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.IssuesHubSidePanel,
    percentage: 100,
    featureflag: ProjectFeatureFlag.LexiconIssuesHubSidePanel,
  }),
  [FeatureRolloutName.EnableCustomRoles]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.EnableCustomRoles,
    percentage: 0,
    featureflag: ProjectFeatureFlag.EnableCustomRoles,
  }),
  [FeatureRolloutName.EnableGovernedAIMode]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.EnableGovernedAIMode,
    percentage: 0,
    featureflag: ProjectFeatureFlag.EnableGovernedAIMode,
  }),
  [FeatureRolloutName.EnableVerifiedCohorts]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.EnableVerifiedCohorts,
    percentage: 50,
    featureflag: ProjectFeatureFlag.EnableVerifiedCohorts,
  }),
  [FeatureRolloutName.WebhookAlerts]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.WebhookAlerts,
    percentage: 100,
    featureflag: ProjectFeatureFlag.WebhookAlerts,
  }),
  [FeatureRolloutName.McpGaTour]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.McpGaTour,
    percentage: 100,
    featureflag: ProjectFeatureFlag.McpGaTour,
  }),
  [FeatureRolloutName.CustomAlertsCharts]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CustomAlertsCharts,
    percentage: 100,
    featureflag: ProjectFeatureFlag.CustomAlertsCharts,
  }),
  [FeatureRolloutName.IntegrationsV2UI]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.IntegrationsV2UI,
    percentage: 0,
    featureflag: ProjectFeatureFlag.IntegrationsV2UI,
  }),
  [FeatureRolloutName.AdvancedDropFilters]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.AdvancedDropFilters,
    percentage: 100,
    featureflag: ProjectFeatureFlag.AdvancedDropFilters,
  }),
};
export function useTemplateSearch() {
  if (isTestEnv()) {
    return true;
  }
  return isUrlDefinedExperimentalFeatureEnabled(getLocation(), `template_search`, () => isCurrentProjectInternal());
}

export function isSRMultiTimelineEnabled(currentProject?: CurrentProject) {
  const variant = getFeatureFlagVariant(FeatureFlag.SessionRecordingMultiTimeline);
  const hasOverride = currentProject?.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingMultiTimeline);
  return hasOverride || (!!variant && variant === FEATURE_FLAG_VARIANTS.Enabled);
}

export function isDataVolumeMonitoringEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return currentProject.getFeatureGate(FeatureGate.DataGovernance);
}

export function getSessionRecordingBlockingInfo(currentProject?: CurrentProject) {
  if (!currentProject) {
    return [];
  }
  return currentProject.blocking.blockedReplayWindows || [];
}

export function isSessionRecordingEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  const hasSessionReplayFeatureGate = currentProject.getFeatureGate(FeatureGate.SessionReplay);
  const hasFeatureFlagEnabled = currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecording);
  const hasSessionReplayEnabled = hasSessionReplayFeatureGate || hasFeatureFlagEnabled;
  if (currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingSensitiveData)) {
    return hasSessionReplayEnabled && currentProject.hasSensitiveAccess;
  }

  return hasSessionReplayEnabled;
}

export const isSessionRecordingRemoteSettingEnabled = (currentProject?: CurrentProject) => {
  const hasOverride = currentProject?.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingRemoteSetting);
  if (hasOverride) {
    return true;
  }
  const variant = getFeatureFlagVariant(FeatureFlag.SessionRecordingRemoteSetting);
  return !!variant && (variant === FEATURE_FLAG_VARIANTS.Enabled || variant === `properties_enabled`);
};

export function isSegmentRegionEnabled() {
  return true;
}

export function isDataStandardsEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return isDevEnv() || currentProject?.getFeatureGate(FeatureGate.DataStandards);
}

export function isAutomateEventApprovalEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return currentProject?.getFeatureGate(FeatureGate.DataStandards);
}

export function isGovernedAIModeEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  return FeatureRollouts[FeatureRolloutName.EnableGovernedAIMode].isEnabled(currentProject);
}

export function isDisableVisibleOnNonCompliantEnabled(currentProject?: CurrentProject | ProjectDetails): boolean {
  if (isDevEnv()) {
    return true;
  }

  if (!currentProject) {
    return false;
  }

  return (
    currentProject.getFeatureGate(FeatureGate.DataStandards) && // has data standards feature gate
    isDataStandardsEnabled(currentProject) && // data standards feature is enabled
    currentProject.getFeatureGate(FeatureGate.DataGovernance) && // has event approval feature gate, this check is necessary in case account downgrade
    currentProject?.hasEventApprovalEnabled && // event approval is enabled and turned on
    FeatureRollouts[FeatureRolloutName.DisableVizOnNonCompliant].isEnabled(currentProject)
  );
}

export function isCompanyProfileEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (isDevEnv()) {
    return true;
  }
  return FeatureRollouts[FeatureRolloutName.CompanyProfile].isEnabled(currentProject);
}

export function isMetricLevelDataGroupsEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (!currentProject) {
    return false;
  }
  return (
    currentProject.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics) ||
    // Also check metric level data groups feature flag if its in a testing environment
    // Till we setup ReportBookmarkManagerProvider for b2b-metrics.stories.tsx
    (currentProject.hasFeatureFlag(ProjectFeatureFlag.MetricLevelDataGroups) && isTestEnv())
  );
}

export function isCanvasEnabled(currentProject: CurrentProject | null) {
  if (!currentProject) {
    return false;
  }
  return currentProject.getFeatureGate(FeatureGate.MetricTree);
}

export function isGoalEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.Goal);
}

export function isNullPropertyMonitoringEnabled(currentProject: CurrentProject | null | undefined) {
  return (
    FeatureRollouts[FeatureRolloutName.NullPropertyMonitoring].isEnabled(currentProject) ||
    isDevEnv() ||
    isCurrentProjectInternal()
  );
}

function getFeatureFlagVariant(flagName: FeatureFlag, fallbackValue?: string) {
  if (typeof mixpanel === `undefined`) {
    return fallbackValue;
  }

  return mixpanel.flags?.get_variant_value_sync(flagName, fallbackValue);
}

/**
 * Check if a feature flag is enabled synchronously.
 * Similar to isFeatureFlagEnabled but uses synchronous check without currentProject.
 */
export function isFeatureFlagEnabledSync(flagName: FeatureFlag, fallbackValue: boolean = false): boolean {
  return mixpanel.flags?.is_enabled_sync(flagName, fallbackValue) ?? fallbackValue;
}

// Use when using an experiment feature flag and checking for enablement.
export function isFeatureFlagVariantEnabled(
  currentProject: CurrentProject | ProjectDetails | null | undefined,
  flagName: FeatureFlag,
  projectFeatureFlagName: ProjectFeatureFlag,
) {
  const hasOverride = currentProject?.hasFeatureFlag(projectFeatureFlagName);
  if (hasOverride) {
    return true;
  }

  const variant = getFeatureFlagVariant(flagName);
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Enabled;
}

// Use when using a feature gate feature flag and checking for enablement.
export function isFeatureFlagEnabled(
  currentProject: CurrentProject | null | undefined,
  flagName: FeatureFlag,
  projectFeatureFlagName: ProjectFeatureFlag,
) {
  return currentProject?.hasFeatureFlag(projectFeatureFlagName) || mixpanel.flags?.is_enabled(flagName, false);
}

export function isIssuesHubEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return (
    FeatureRollouts[FeatureRolloutName.IssuesHub].isEnabled(currentProject ?? null) ||
    isDevEnv() ||
    isCurrentProjectInternal()
  );
}

export function isIssuesHubSidePanelEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return (
    FeatureRollouts[FeatureRolloutName.IssuesHubSidePanel].isEnabled(currentProject ?? null) ||
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.LexiconIssuesHubSidePanel)
  );
}

export function isPropertyTypeMonitoringEnabled(currentProject: CurrentProject | null | undefined) {
  return (
    FeatureRollouts[FeatureRolloutName.PropertyTypeMonitoring].isEnabled(currentProject) ||
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.PropertyTypeMonitoring)
  );
}

export function isVerifiedCohortsEnabled(currentProject?: CurrentProject | ProjectDetails | null): boolean {
  return (
    FeatureRollouts[FeatureRolloutName.EnableVerifiedCohorts].isEnabled(currentProject ?? null) ||
    Boolean(currentProject?.hasFeatureFlag(ProjectFeatureFlag.EnableVerifiedCohorts))
  );
}

export function isAdvancedDeletionEventCountBypassEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.AdvDelEventCountBypass);
}

export function isAdvancedDeletionRequestPerMonthBypassEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.AdvDelRequestPerMonthBypass);
}

export function isJHSAdvancedDropEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.JHSAdvancedDrop);
}

export function isAdvancedDropFiltersEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return (
    FeatureRollouts[FeatureRolloutName.AdvancedDropFilters].isEnabled(currentProject) ||
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.AdvancedDropFilters)
  );
}

export function isBlockedAndFilteredStatusEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.BlockedAndFilteredStatus);
}

export function isAllEventsDisabled(currentProject: CurrentProject | undefined): boolean {
  return Boolean(currentProject?.hasFeatureFlag(ProjectFeatureFlag.DisableAllEvents));
}

export function isBoardsMadeEasyEnabled(currentProject: CurrentProject | undefined): boolean {
  return (
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.BoardsMadeEasy) ||
    mixpanel.flags?.get_variant_value_sync(FeatureFlag.BoardsMadeEasy, false)
  );
}

export function isRetentionAwarenessEnabled(currentProject: CurrentProject | undefined): boolean {
  if (currentProject?.hasFeatureFlag(ProjectFeatureFlag.RetentionAwareness)) {
    return true;
  }
  const RETENTION_AWARENESS_CACHE_KEY = `$mp_retention_awareness_flag`;

  // Caching this feature flag since it gets evaluated on every project metadata load
  // and we want to avoid sending redundant $experiment_started events
  try {
    const cached = window.sessionStorage.getItem(RETENTION_AWARENESS_CACHE_KEY);
    if (cached) {
      return cached === FEATURE_FLAG_VARIANTS.Enabled;
    }
  } catch {
    // fall through to SDK call
  }

  const variant = getFeatureFlagVariant(FeatureFlag.RetentionAwareness);
  try {
    if (variant) {
      window.sessionStorage.setItem(RETENTION_AWARENESS_CACHE_KEY, variant);
    }
  } catch {
    // continue without caching
  }
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Enabled;
}

export function isChartTrendlinesEnabled(currentProject: CurrentProject | undefined): boolean {
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.ChartTrendlines) || isCurrentProjectInternal();
}

export function isWebhookAlertsEnabled(currentProject?: CurrentProject | ProjectDetails | null): boolean {
  return FeatureRollouts[FeatureRolloutName.WebhookAlerts].isEnabled(currentProject ?? null);
}

export function isVersionHistoryEnabled(currentProject?: CurrentProject): boolean {
  return (
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.VersionHistory) ||
    mixpanel.flags?.get_variant_value_sync(FeatureFlag.VersionHistory, false)
  );
}

export function isContentDiagnosticsTabEnabled(currentProject?: CurrentProject | null): boolean {
  return (
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.ContentDiagnosticsTab) ||
    isFeatureFlagEnabledSync(FeatureFlag.ContentDiagnosticsTab)
  );
}

export function isPasskeysEnabled(currentProject?: CurrentProject | null): boolean {
  return Boolean(currentProject?.hasFeatureFlag(ProjectFeatureFlag.EnablePasskeyCreation));
}

export function isSparkReportAutoNameEnabled(currentProject?: CurrentProject): boolean {
  const legacyCheck = currentProject?.hasFeatureFlag(ProjectFeatureFlag.SparkReportAutoName);
  if (legacyCheck) {
    return true;
  }

  const variant = getFeatureFlagVariant(FeatureFlag.SparkReportAutoName);
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Treatment;
}

export function isSparkBoardSummaryEnabled(): boolean {
  if (isDevEnv()) {
    return true;
  }

  const variant = getFeatureFlagVariant(FeatureFlag.SparkBoardSummary);
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Treatment;
}

export function isSparkCohortAutoNameEnabled(currentProject?: CurrentProject): boolean {
  const legacyCheck = currentProject?.hasFeatureFlag(ProjectFeatureFlag.SparkCohortAutoName);
  if (legacyCheck) {
    return true;
  }

  const variant = getFeatureFlagVariant(FeatureFlag.SparkCohortAutoName);
  return !!variant && variant === FEATURE_FLAG_VARIANTS.Treatment;
}

export function isSparkCreateNewReportEnabled(): boolean {
  return isFeatureFlagEnabledSync(FeatureFlag.SparkCreateNewReport);
}

export function isSparkCreateBoardEnabled(): boolean {
  return isFeatureFlagEnabledSync(FeatureFlag.SparkCreateBoard);
}

export function isSparkCopilotEnabled(currentProject?: CurrentProject): boolean {
  if (currentProject?.hasFeatureFlag(ProjectFeatureFlag.SparkUserRollout)) {
    return isFeatureFlagEnabledSync(FeatureFlag.SparkUserRollout);
  }

  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.SparkCopilot) ?? false;
}
